import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Grid,
  Heading,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Lock, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { palette } from "./ui";
import { useRegisterFianzaInterest } from "../hooks/useFetchAnalytics";
import { track } from "@/helpers/posthog";

export type PaywallTenant = {
  tenant: string;
  contract: string;
  amount: string;
  signal: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  tenant: PaywallTenant | null;
};

/**
 * The sections below the header sit behind a blur and exist to show what the
 * paid tenant profile would contain. They are intentionally fixed sample
 * content: nothing real is sent to the browser for a feature the merchant has
 * not bought, and the shapes are all that survive the blur anyway.
 */
const SAMPLE_HISTORY = [
  { month: "Sep", status: "paid", amount: "$2.2M" },
  { month: "Oct", status: "late", amount: "$2.1M" },
  { month: "Nov", status: "paid", amount: "$2.3M" },
  { month: "Dic", status: "paid", amount: "$2.4M" },
  { month: "Ene", status: "paid", amount: "$2.2M" },
  { month: "Feb", status: "pending", amount: "$2.4M" },
];

const SAMPLE_SIGNALS = [
  { label: "Dia habitual de pago", value: "Dia 2-3", detail: "Patron estable" },
  { label: "Historial", value: "12 pagos", detail: "0 reversados" },
  { label: "Ultima actividad", value: "Hace 6 dias", detail: "Abrio el link" },
  { label: "Riesgo operativo", value: "Alto", detail: "Fuera de patron" },
];

const SAMPLE_BEHAVIORAL = [
  { label: "Puntualidad historica", value: "92%", bar: 92 },
  { label: "Friccion este mes", value: "Alta", bar: 70 },
  { label: "Dias en mora actual", value: "5", bar: 40 },
  { label: "Confianza", value: "Media", bar: 55 },
];

const statusDots: Record<string, string> = {
  paid: palette.green,
  late: palette.yellow,
  failed: palette.red,
  pending: palette.muted,
};

const PaywallModal = ({ open, onClose, tenant }: Props): JSX.Element => {
  const registerInterest = useRegisterFianzaInterest();
  const [interestSent, setInterestSent] = useState(false);
  const [lastTenant, setLastTenant] = useState(tenant);
  useEffect(() => {
    if (tenant) setLastTenant(tenant);
  }, [tenant]);

  // Never early-return: the dialog must be mounted (and closed) before it opens,
  // otherwise Chakra mounts it already-open, skips the enter transition and the
  // scroll lock it puts on <body> is never released.
  const shown = tenant ??
    lastTenant ?? { tenant: "", contract: "", amount: "", signal: "" };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => !e.open && onClose()}
      size="lg"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bg="white"
            borderRadius="12px"
            maxW="720px"
            maxH="90vh"
            overflow="hidden"
            position="relative"
          >
            <Dialog.Header
              borderBottom="1px solid #EBECEF"
              px="6"
              py="4"
              alignItems="flex-start"
            >
              <Flex gap={4} align="center" w="100%">
                <Box
                  w="48px"
                  h="48px"
                  borderRadius="full"
                  bg="#F3F5FC"
                  color={palette.muted}
                  flex="none"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="20px"
                  fontWeight="800"
                >
                  {shown.tenant[0]}
                </Box>
                <Box>
                  <Dialog.Title>
                    <Heading size="md" color={palette.text}>
                      {shown.tenant}
                    </Heading>
                  </Dialog.Title>
                  <Text color={palette.muted} fontSize="13px" mt="1">
                    {shown.contract} · {shown.amount}
                  </Text>
                </Box>
              </Flex>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton
                position="absolute"
                top="4"
                right="4"
                onClick={onClose}
              />
            </Dialog.CloseTrigger>

            <Dialog.Body px="0" py="0">
              <Box position="relative" maxH="60vh" overflow="hidden">
                <Box
                  filter="blur(6px)"
                  opacity={0.4}
                  pointerEvents="none"
                  px="6"
                  py="5"
                  userSelect="none"
                >
                  <VStack gap={5} align="stretch">
                    <Box>
                      <Text
                        fontSize="11px"
                        fontWeight="700"
                        color={palette.label}
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                        mb={3}
                      >
                        Historial de pagos
                      </Text>
                      <Grid templateColumns="repeat(6, 1fr)" gap={2}>
                        {SAMPLE_HISTORY.map((item, i) => (
                          <Box
                            key={i}
                            bg="#F3F5FC"
                            borderRadius="8px"
                            p={3}
                            textAlign="center"
                          >
                            <Text
                              fontSize="11px"
                              color={palette.muted}
                              fontWeight="600"
                              mb={2}
                            >
                              {item.month}
                            </Text>
                            <Box
                              w="24px"
                              h="24px"
                              borderRadius="full"
                              bg={statusDots[item.status] ?? palette.muted}
                              mx="auto"
                              mb={2}
                            />
                            <Text
                              fontSize="12px"
                              fontWeight="700"
                              color={palette.muted}
                            >
                              {item.amount}
                            </Text>
                          </Box>
                        ))}
                      </Grid>
                    </Box>
                    <Box>
                      <Text
                        fontSize="11px"
                        fontWeight="700"
                        color={palette.label}
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                        mb={3}
                      >
                        Señales registradas
                      </Text>
                      <Grid templateColumns="1fr 1fr" gap={3}>
                        {SAMPLE_SIGNALS.map((sig, i) => (
                          <Box
                            key={i}
                            bg="#F3F5FC"
                            borderRadius="8px"
                            p={3}
                            border="1px solid #EBECEF"
                          >
                            <Text fontSize="12px" color={palette.muted} mb={1}>
                              {sig.label}
                            </Text>
                            <Text
                              fontSize="14px"
                              fontWeight="700"
                              color={palette.text}
                            >
                              {sig.value}
                            </Text>
                            <Text fontSize="11px" color={palette.muted} mt={1}>
                              {sig.detail}
                            </Text>
                          </Box>
                        ))}
                      </Grid>
                    </Box>
                    <Box>
                      <Text
                        fontSize="11px"
                        fontWeight="700"
                        color={palette.label}
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                        mb={3}
                      >
                        Perfil comportamental
                      </Text>
                      <Grid templateColumns="1fr 1fr" gap={3}>
                        {SAMPLE_BEHAVIORAL.map((b, i) => (
                          <Box key={i}>
                            <Flex
                              justify="space-between"
                              mb={1}
                              fontSize="12px"
                            >
                              <Text color={palette.muted}>{b.label}</Text>
                              <Text fontWeight="700" color={palette.text}>
                                {b.value}
                              </Text>
                            </Flex>
                            <Box
                              h="6px"
                              bg="#F3F5FC"
                              borderRadius="full"
                              overflow="hidden"
                            >
                              <Box
                                h="100%"
                                w={`${b.bar}%`}
                                bg={palette.purple}
                                borderRadius="full"
                              />
                            </Box>
                          </Box>
                        ))}
                      </Grid>
                    </Box>
                  </VStack>
                </Box>

                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="rgba(255, 255, 255, 0.85)"
                >
                  <VStack
                    gap={4}
                    align="center"
                    textAlign="center"
                    maxW="420px"
                    px={6}
                  >
                    <Flex
                      w="56px"
                      h="56px"
                      borderRadius="full"
                      bg={palette.soft}
                      align="center"
                      justify="center"
                      color={palette.purple}
                    >
                      <Lock size={24} />
                    </Flex>
                    <Box>
                      <Heading size="md" color={palette.text} mb={2}>
                        Perfil del inquilino
                      </Heading>
                      <Text
                        color={palette.muted}
                        fontSize="14px"
                        lineHeight="1.5"
                      >
                        El detalle del perfil del inquilino — historial de
                        pagos, scoring comportamental, señales de riesgo y
                        contexto operativo — es parte del{" "}
                        <Text as="span" fontWeight="700" color={palette.purple}>
                          Plan Fianza Palomma
                        </Text>
                        .
                      </Text>
                    </Box>
                    <Flex gap={3} align="center" wrap="wrap" justify="center">
                      <Flex
                        gap={2}
                        align="center"
                        bg={palette.soft}
                        px={3}
                        py={2}
                        borderRadius="full"
                      >
                        <Sparkles size={14} color={palette.purple} />
                        <Text
                          fontSize="12px"
                          fontWeight="600"
                          color={palette.purple}
                        >
                          Scoring de inquilinos
                        </Text>
                      </Flex>
                      <Flex
                        gap={2}
                        align="center"
                        bg={palette.soft}
                        px={3}
                        py={2}
                        borderRadius="full"
                      >
                        <Sparkles size={14} color={palette.purple} />
                        <Text
                          fontSize="12px"
                          fontWeight="600"
                          color={palette.purple}
                        >
                          Historial completo
                        </Text>
                      </Flex>
                      <Flex
                        gap={2}
                        align="center"
                        bg={palette.soft}
                        px={3}
                        py={2}
                        borderRadius="full"
                      >
                        <Sparkles size={14} color={palette.purple} />
                        <Text
                          fontSize="12px"
                          fontWeight="600"
                          color={palette.purple}
                        >
                          Señales de riesgo
                        </Text>
                      </Flex>
                    </Flex>
                    <Button
                      size="lg"
                      bg={palette.purple}
                      color="white"
                      borderRadius="full"
                      _hover={{ bg: palette.purpleDark }}
                      mt={2}
                      disabled={interestSent}
                      onClick={() => {
                        track("fianza_interest_clicked", {
                          signal: shown.signal,
                        });
                        registerInterest();
                        setInterestSent(true);
                      }}
                    >
                      {interestSent
                        ? "Solicitud registrada"
                        : "Conocer Plan Fianza Palomma"}
                    </Button>
                    {interestSent && (
                      <Text fontSize="12px" color={palette.muted}>
                        Un asesor de Palomma se pondra en contacto.
                      </Text>
                    )}
                    <Text fontSize="12px" color={palette.muted}>
                      Perfil de {shown.tenant} · {shown.signal}
                    </Text>
                  </VStack>
                </Box>
              </Box>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default PaywallModal;
